/**
 * Inline params.yaml editor for a v0.6 project.
 *
 * Three sections — weld, classification, toolpath — matching the
 * ProjectParams dataclass. Save PATCHes the full ProjectParamsPayload
 * back; granular dirty-field tracking is deliberately skipped.
 */
import { useEffect, useState } from 'react'
import {
  Button,
  Callout,
  Card,
  Divider,
  FormGroup,
  NumericInput,
} from '@blueprintjs/core'

import {
  getProjectParams,
  patchProjectParams,
  type ProjectParamsPayload,
} from '../api'


export function ProjectParamsEditor({ projectId }: { projectId: string }) {
  const [params, setParams] = useState<ProjectParamsPayload | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [dirty, setDirty] = useState(false)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    getProjectParams(projectId).then(setParams).catch((ex) => setErr(String(ex)))
  }, [projectId])

  const save = async () => {
    if (!params) return
    setSaving(true)
    setErr(null)
    try {
      const updated = await patchProjectParams(projectId, params)
      setParams(updated)
      setDirty(false)
    } catch (ex) {
      setErr(ex instanceof Error ? ex.message : String(ex))
    } finally {
      setSaving(false)
    }
  }

  const mutate = (updater: (p: ProjectParamsPayload) => ProjectParamsPayload) => {
    if (!params) return
    setParams(updater(params))
    setDirty(true)
  }

  if (err && !params) return <Callout intent="danger" icon="error">{err}</Callout>
  if (!params) return <div style={{ padding: 12 }}>Loading parameters…</div>

  return (
    <Card style={{ marginTop: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <h3 style={{ margin: 0 }}>Parameters</h3>
        <Button
          icon="floppy-disk"
          intent={dirty ? 'primary' : 'none'}
          disabled={!dirty || saving}
          loading={saving}
          onClick={save}
        >
          Save
        </Button>
      </div>
      {err && <Callout intent="danger" icon="error" style={{ marginTop: 8 }}>{err}</Callout>}

      <Divider />

      <h4>Weld</h4>
      <FormGroup label="Travel speed (mm/s)">
        <NumericInput
          minorStepSize={0.1} stepSize={0.5} majorStepSize={5}
          value={params.weld.travel_speed_mm_s}
          onValueChange={(v) =>
            mutate((p) => ({ ...p, weld: { ...p.weld, travel_speed_mm_s: v || 0 } }))}
        />
      </FormGroup>
      <FormGroup label="Current (A)">
        <NumericInput
          stepSize={5} majorStepSize={25}
          value={params.weld.current_a}
          onValueChange={(v) =>
            mutate((p) => ({ ...p, weld: { ...p.weld, current_a: v || 0 } }))}
        />
      </FormGroup>
      <FormGroup label="Voltage (V)">
        <NumericInput
          minorStepSize={0.1} stepSize={0.5} majorStepSize={2}
          value={params.weld.voltage_v}
          onValueChange={(v) =>
            mutate((p) => ({ ...p, weld: { ...p.weld, voltage_v: v || 0 } }))}
        />
      </FormGroup>
      <FormGroup label="Weave amplitude (mm)">
        <NumericInput
          minorStepSize={0.1} stepSize={0.5} majorStepSize={2}
          value={params.weld.weave_amplitude_mm}
          onValueChange={(v) =>
            mutate((p) => ({ ...p, weld: { ...p.weld, weave_amplitude_mm: v || 0 } }))}
        />
      </FormGroup>
      <FormGroup label="Weave frequency (Hz)">
        <NumericInput
          minorStepSize={0.1} stepSize={0.5} majorStepSize={2}
          value={params.weld.weave_frequency_hz}
          onValueChange={(v) =>
            mutate((p) => ({ ...p, weld: { ...p.weld, weave_frequency_hz: v || 0 } }))}
        />
      </FormGroup>

      <Divider />

      <h4>Classification</h4>
      <FormGroup label="Face normal tolerance (deg)" helperText="Max angle between a face normal and the hardface direction for the face to be classified.">
        <NumericInput
          minorStepSize={0.5} stepSize={1} majorStepSize={5}
          value={params.classification.hardface_face_normal_tol_deg}
          onValueChange={(v) =>
            mutate((p) => ({ ...p, classification: { ...p.classification, hardface_face_normal_tol_deg: v || 0 } }))}
        />
      </FormGroup>
      <FormGroup label="Minimum face area (mm²)" helperText="Faces smaller than this are dropped as noise.">
        <NumericInput
          stepSize={10} majorStepSize={50}
          value={params.classification.min_face_area_mm2}
          onValueChange={(v) =>
            mutate((p) => ({ ...p, classification: { ...p.classification, min_face_area_mm2: v || 0 } }))}
        />
      </FormGroup>

      <Divider />

      <h4>Toolpath</h4>
      <FormGroup label="Stepover (mm)">
        <NumericInput
          minorStepSize={0.1} stepSize={0.5} majorStepSize={2}
          value={params.toolpath.stepover_mm}
          onValueChange={(v) =>
            mutate((p) => ({ ...p, toolpath: { ...p.toolpath, stepover_mm: v || 0 } }))}
        />
      </FormGroup>
      <FormGroup label="Overlap (%)">
        <NumericInput
          min={0} max={100}
          stepSize={5} majorStepSize={10}
          value={params.toolpath.overlap_pct}
          onValueChange={(v) =>
            mutate((p) => ({ ...p, toolpath: { ...p.toolpath, overlap_pct: v || 0 } }))}
        />
      </FormGroup>
    </Card>
  )
}
